import api from "@/services/api";

const { post, put, get, delete: del } = api.getPrefixedMethods("orderinfo");

// Top-level OrderInfo DateField columns. HTML <input type="date"> v-models become ""
// when the user clears the field, but the save-order endpoint rejects "" with 400
// (LAM-3863) because "" is indistinguishable from "no value" at the wire layer.
// Normalize to null here so cleared dates round-trip as a no-op (or explicit clear)
// instead of failing the save.
const ORDER_INFO_DATE_FIELDS = ["patientDob", "treatmentStartDate"];

function normalizeOrderInfoDateFields(data) {
  if (!data || typeof data !== "object") return data;
  const normalized = { ...data };
  for (const field of ORDER_INFO_DATE_FIELDS) {
    if (normalized[field] === "") {
      normalized[field] = null;
    }
  }
  return normalized;
}

export const orderInfoService = {
  submitOrderInfo(data) {
    return post("", data);
  },
  validateAccess(data) {
    return post("access-initiated-prior-auth", data);
  },
  getListOfOrders(index, status, patient, date, dateFrom, dateTo, orderBy, tags, pageSize, modifiedBy, assignedUser) {
    const params = { page: index, status };
    if (patient) params.patient = patient;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (!dateFrom && !dateTo && date) params.date = date;
    if (orderBy) params.order_by = orderBy;
    if (tags) params.tags = tags;
    if (pageSize) params.page_size = pageSize;
    if (modifiedBy) params.modified_by = modifiedBy;
    if (assignedUser) params.assigned_user = assignedUser;
    return get("", { params });
  },
  getOrderStatusCounts(filters = {}) {
    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    return get("submission-counts", { params });
  },
  uploadDecisionDocument(orderKey, data) {
    return put(`upload-decision-document/${orderKey}`, data);
  },
  draftAppeal(orderKey, data) {
    return post(`submission/${orderKey}/draft-appeal/`, data);
  },
  getAppealDraft(orderKey, taskId) {
    return get(`submission/${orderKey}/draft-appeal/`, { params: { task_id: taskId } });
  },
  saveAppealDraft(orderKey, data) {
    return put(`submission/${orderKey}/draft-appeal/`, data);
  },
  // Autofill the create-submission form from the order's uploaded documents (LAM-4379).
  // The response's field names arrive camelized, so they line up with orderInfo's own keys.
  startCreateSubmissionAutofill(orderKey, data) {
    return post(`submission/${orderKey}/autofill/`, data);
  },
  getCreateSubmissionAutofill(orderKey, taskId) {
    return get(`submission/${orderKey}/autofill/`, { params: { task_id: taskId } });
  },
  changeOrderFormulary(orderKey, data) {
    return put(`change-order-formulary/${orderKey}`, data);
  },
  changeFormSelection(orderKey, data) {
    return put(`change-form-selection/${orderKey}`, data);
  },
  reopenSubmission(orderKey, data) {
    return put(`reopen-submission/${orderKey}`, normalizeOrderInfoDateFields(data));
  },
  searchOrders(searchTerm, index, pageSize) {
    const params = { page: index };
    if (pageSize) params.page_size = pageSize;
    return post("search", searchTerm, { params });
  },
  getOrderDetails(id) {
    return get(`submission/${id}`);
  },
  getFreshDocumentLink(orderKey, url) {
    return post(`submission/${orderKey}/document-link`, { url });
  },
  getOrderAuditLogs(orderKey, pagingOptions) {
    return get(`submission/${orderKey}/audits`, { params: pagingOptions });
  },
  startOrderStaffTime(documentKey) {
    return post(`submission/${documentKey}/staff-time`);
  },
  pushOrderStaffTime(documentKey) {
    return del(`submission/${documentKey}/staff-time`);
  },
  pullAutofillingData(orderKey, data) {
    return post(`submit-autofill-data/${orderKey}`, data);
  },
  getAvailableAddressBooks() {
    return get("available-address-books");
  },
  createEmptyBenefitVerificationRequest(data) {
    return post("create-benefit-verification", data);
  },
  saveBenefitVerificationRequest(orderKey, data) {
    return put(`save-benefit-verification/${orderKey}`, data);
  },
  createEmptyOrder(data) {
    return post("create-empty-order", data);
  },
  createNewOrder(data) {
    return post("create-order", data);
  },
  saveOrder(orderKey, data) {
    return put(`save-order/${orderKey}`, normalizeOrderInfoDateFields(data));
  },
  applyPatientField(orderKey, field) {
    return post(`submission/${orderKey}/apply-patient-field/`, { field });
  },
  checkProviderDelegateAgreement(orderKey, providerFields) {
    return post(`submission/${orderKey}/provider-delegate-agreement-check/`, providerFields);
  },
  submitPdfForm(data) {
    return post("pdf-submission", data);
  },
  getPdfSubmissionDetails(id) {
    return get(`pdf-submission/${id}`);
  },
  updatePdfSubmission(id, data) {
    return put(`pdf-submission/${id}`, data);
  },
  fetchPatientRecords(orderKey) {
    return get(`patient-records/${orderKey}`);
  },
  deleteOrder(orderKey) {
    return del(`submission/${orderKey}`);
  },
  getSupportingDocumentsPdf(orderKey) {
    return get(`supporting-documents-pdf/${orderKey}`, {
      responseType: "blob",
      headers: { "Content-Type": "application/pdf" },
      _returnFullResponse: true,
      _skipPropsAdaptation: true,
    });
  },
  getFilledFormPdf(orderKey) {
    return get(`filled-form-pdf/${orderKey}`, {
      responseType: "blob",
      headers: { "Content-Type": "application/pdf" },
      _returnFullResponse: true,
      _skipPropsAdaptation: true,
    });
  },
  archiveOrder(orderKey) {
    return put(`submission/${orderKey}/archive/`);
  },
  unarchiveOrder(orderKey) {
    return del(`submission/${orderKey}/archive/`);
  },
  changeSubmissionMethod(orderKey, data) {
    return put(`submission/${orderKey}/change-submission-method/`, data);
  },
  changeRecordedStatus(orderKey, data) {
    return put(`submission/${orderKey}/change-recorded-status/`, data);
  },
  sendToPrescriber(orderKey, data) {
    return post(`send-to-prescriber/${orderKey}/`, data);
  },
  importCMMSubmission(data) {
    return post("cmm-submissions/", data);
  },
  updateSubmissionTags(orderKey, tags) {
    return put(`submission/${orderKey}/tags/`, { tags });
  },
  reportSubmissionIssue(orderKey, data) {
    return post(`submission/${orderKey}/report-issue/`, data);
  },
  acquireOrderLock(orderKey) {
    return post(`submission/${orderKey}/locks/`);
  },
  releaseOrderLock(orderKey) {
    return del(`submission/${orderKey}/locks/`);
  },
};
